import { GlForm, GlFormFields, GlButton, GlSprintf } from '@gitlab/ui';
import { formValidators } from '@gitlab/ui/src/utils';
import illustration from '@gitlab/svgs/dist/illustrations/email-verification-md.svg?url';
import { s__ } from '~/locale';
import { createAlert, VARIANT_SUCCESS } from '~/alert';
import { visitUrl } from '~/lib/utils/url_utility';
import { applyDeepLinkFragment } from '~/authentication/sessions/post_signin_fragment';
import axios from '~/lib/utils/axios_utils';
import GlCountdown from '~/vue_shared/components/gl_countdown.vue';
import { I18N_INPUT_LABEL, I18N_EMAIL_EMPTY_CODE, I18N_EMAIL_INVALID_CODE, I18N_SUBMIT_BUTTON, I18N_DIDNT_GET_CODE_RESEND_LINK, I18N_RESEND_CODE, I18N_RESEND_CODE_IN, I18N_EMAIL_RESEND_SUCCESS, I18N_GENERIC_ERROR, I18N_SEND_TO_SECONDARY_EMAIL_GUIDE, VERIFICATION_CODE_REGEX, SUCCESS_RESPONSE } from '~/sessions/new/constants';
import EmailForm from '~/sessions/new/components/email_form.vue';
import { PASSWORD_MANAGER_IGNORE_ATTRS } from '~/lib/utils/forms';
import { newUserSessionPath } from '~/lib/utils/path_helpers/routes';
import VerificationLayout from './verification_layout.vue';
const VERIFY_CODE_FORM = 'VERIFY_CODE_FORM';
const ANOTHER_EMAIL_FORM = 'ANOTHER_EMAIL_FORM';
const FIELD_KEY = 'verificationCode';
export default {
    name: 'EmailCode',
    components: {
        GlForm,
        GlFormFields,
        GlButton,
        GlSprintf,
        GlCountdown,
        EmailForm,
        VerificationLayout
    },
    props: {
        sendEmailOtpPath: {
            type: String,
            required: true
        },
        emailVerificationData: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            email: this.emailVerificationData.obfuscatedEmail,
            values: {
                [FIELD_KEY]: ''
            },
            verifyError: '',
            activeForm: VERIFY_CODE_FORM,
            isSending: false,
            // Tracks the auto-send only. `isSending` also covers resends, which must not block
            // verifying a code from an earlier send.
            initialSendComplete: false,
            showResendAfter: 0
        };
    },
    computed: {
        serverValidations () {
            return {
                [FIELD_KEY]: this.verifyError
            };
        },
        showResend () {
            return new Date(this.showResendAfter) <= new Date();
        },
        resendAfterDateString () {
            return new Date(this.showResendAfter).toString();
        }
    },
    watch: {
        // Clear a stale server error as soon as the user edits the code.
        'values.verificationCode': function clearVerifyError() {
            this.verifyError = '';
        }
    },
    mounted () {
        // Held so verify() can wait for it.
        this.sendCodePromise = this.sendCode();
    },
    methods: {
        newUserSessionPath,
        async sendCode () {
            this.isSending = true;
            try {
                const { data } = await axios.post(this.sendEmailOtpPath, {
                    user: {
                        login: this.emailVerificationData.username
                    }
                });
                this.showResendAfter = data.show_resend_after ?? 0;
            } catch (error) {
                const message = error?.response?.data?.message;
                if (message) {
                    this.showAlert({
                        message
                    });
                } else {
                    this.handleError(error);
                }
            } finally{
                this.isSending = false;
                this.initialSendComplete = true;
            }
        },
        async verify () {
            // The auto-send is what moves the session from the two-factor attempt to email
            // verification; a code submitted before it lands is handled by the wrong controller
            // branch and comes back as an unusable generic failure. The verify button stays
            // disabled until then, but that only covers the paths a user takes: a programmatic
            // form submit reaches here regardless, so wait for a settled session here too.
            // sendCode() swallows its own errors, so this always resolves.
            await this.sendCodePromise;
            try {
                const { data } = await axios.post(this.emailVerificationData.verifyPath, {
                    user: {
                        verification_token: this.values[FIELD_KEY]
                    }
                });
                if (data.status === SUCCESS_RESPONSE) {
                    visitUrl(applyDeepLinkFragment(data.redirect_path));
                } else {
                    this.handleError();
                }
            } catch (error) {
                const message = error?.response?.data?.message;
                if (message) {
                    this.verifyError = message;
                } else {
                    this.handleError(error);
                }
            }
        },
        async resend (email = '') {
            this.isSending = true;
            try {
                const { data } = await axios.post(this.emailVerificationData.resendPath, {
                    user: {
                        email
                    }
                });
                if (data.status === SUCCESS_RESPONSE) {
                    this.showResendAfter = data.show_resend_after ?? 0;
                    this.showAlert({
                        message: I18N_EMAIL_RESEND_SUCCESS,
                        variant: VARIANT_SUCCESS
                    });
                } else {
                    this.handleError();
                }
            } catch (error) {
                const message = error?.response?.data?.message;
                if (message) {
                    this.showAlert({
                        message
                    });
                } else {
                    this.handleError(error);
                }
            } finally{
                this.isSending = false;
                this.resetForm();
            }
        },
        sendToAnotherEmail (email) {
            this.showVerifyCodeForm(email);
            this.resend(email);
        },
        onResendTimerExpired () {
            this.showResendAfter = 0;
        },
        handleError (error) {
            this.showAlert({
                message: I18N_GENERIC_ERROR,
                captureError: true,
                error
            });
        },
        // At most one alert stands at a time; dismissing the outgoing one keeps `this.alert`
        // pointing at what is on screen, whatever createAlert does with the element it replaces.
        showAlert (options) {
            this.dismissAlert();
            this.alert = createAlert(options);
        },
        // A standing alert goes stale once the user switches forms, so it is dropped. Only this
        // component's own alert is dismissed; a server-rendered flash explaining how the user
        // got here is left alone.
        dismissAlert () {
            this.alert?.dismiss();
            this.alert = null;
        },
        resetForm () {
            this.values = {
                [FIELD_KEY]: ''
            };
            this.verifyError = '';
            this.$nextTick(()=>{
                document.getElementById('email-code')?.focus();
            });
        },
        showVerifyCodeForm (email = '') {
            this.dismissAlert();
            this.activeForm = this.$options.forms.verifyCodeForm;
            if (email.length) this.email = email;
            this.resetForm();
        },
        showAnotherEmailForm () {
            this.dismissAlert();
            this.activeForm = this.$options.forms.anotherEmailForm;
        }
    },
    illustration,
    fields: {
        [FIELD_KEY]: {
            id: 'email-code',
            label: I18N_INPUT_LABEL,
            validators: [
                formValidators.required(I18N_EMAIL_EMPTY_CODE),
                (value)=>!value || VERIFICATION_CODE_REGEX.test(value) ? '' : I18N_EMAIL_INVALID_CODE
            ],
            inputAttrs: {
                ...PASSWORD_MANAGER_IGNORE_ATTRS,
                autocomplete: 'one-time-code',
                autofocus: true,
                'data-testid': 'email-code-field',
                inputmode: 'numeric',
                maxlength: '6',
                placeholder: s__('TwoFactorAuth|Enter 6 digit code')
            }
        }
    },
    i18n: {
        title: s__('TwoFactorAuth|Authenticate with your email'),
        description: s__('TwoFactorAuth|We sent a code to %{email} to verify your identity. Keep this window open while checking your code.'),
        submitButton: I18N_SUBMIT_BUTTON,
        useAnotherEmailButton: s__('TwoFactorAuth|Use another email'),
        didntGetCodeResendLink: I18N_DIDNT_GET_CODE_RESEND_LINK,
        resendCodeIn: I18N_RESEND_CODE_IN,
        resendCode: I18N_RESEND_CODE,
        anotherEmailGuide: I18N_SEND_TO_SECONDARY_EMAIL_GUIDE
    },
    forms: {
        verifyCodeForm: VERIFY_CODE_FORM,
        anotherEmailForm: ANOTHER_EMAIL_FORM
    }
};
